'use client'
import { v4 as uuidv4 } from 'uuid'
import {
  deleteLocalData,
  getCookie,
  getLocalData,
  setCookie,
  setLocalData
} from './localData'
import User from '@/types/User';
import CookieManager from '@/utils/cookie';

/**
 * Преобразует строку query-параметров в объект { параметр: значение }.
 * @param {string} value
 */
function query(value: string): Record<string, string> {
  const queryString = /^\??(.*)/.exec(value || '')?.[1] || ''

  return queryString.split('&').reduce(
    (acc, pair) => {
      const [key, val] = pair.split('=')
      if (key) acc[key] = val || '' // Обрабатываем случай, если нет значения
      return acc
    },
    {} as Record<string, string>
  )
}

/**
 * Функция принимает URL и возвращает первую часть его относительного пути (pathname).
 */
function getFirstPartOfPath(url: URL) {
  // Разделяем путь на сегменты, удаляя пустые сегменты
  const [firstPart] = url.pathname
    .split('/')
    .filter((segment) => segment.length > 0)

  return firstPart ?? ''
}

/**
 * Тип дополнительных данных для передачи через pushEvent или pushData.
 */
export type ProAnalyticsEventCustomProps = Record<
  string,
  string | number | boolean | string[] | number[] | boolean[]
>

type PushData = { name: string | string[] } & ProAnalyticsEventCustomProps

class ProAnalytics {
  /**
   * Время жизни сессии
   * (по умолчанию 30 min).
   */
  private sessionDuration = 1800000

  /**
   * Название сессионной куки.
   */
  private sessionCookieName = 'user_session'

  /**
   * Ключ сессионного хранилища для стека переходов между страницами.
   */
  private reflogStorageName = 'proAnalyticsReflog'

  /**
   * Максимальное количество сохраняемых в лог записей о переходах.
   * Выбирается эмпирически.
   *
   * Если записей будет слишком мало, это может приводить к потерям связей
   * между pageViewId и referrerPageViewId, то есть к неточностям в аналитике.
   * Если слишком много, то не исключены проблемы с производительностью на клиенте.
   */
  private reflogSize = 20

  /**
   * Флаг успешной инициализации аналитики.
   * Нельзя отправлять события через неинициализированный экземпляр.
   */
  private hasStarted = false

  /**
   * Уникальный идентификатор просмотра страницы.
   */
  private pageViewId = ProAnalytics.generatePageViewId()

  /**
   * Уникальный идентификатор просмотра страницы, с которой мы пришли.
   */
  private referrerPageViewId?: string

  /**
   * Код состояния ответа HTTP.
   */
  private statusCode: number

  /**
   * Является ли клиент ботом поисковой системы.
   * Значение isSearchSystem доступно только на страницах выдачи.
   */
  private isBot: boolean

  /**
   * Начальная локация для отслеживания переходов через History API.
   */
  private href: string

  /**
   * Название раздела, или просто группы страниц, для удобства построения отчётов.
   * Примеры: catalog, makers, autoProducts.
   */
  private pathType: string | null

  /**
   * Название раздела, из которого мы пришли.
   * Оно восстанавливается из сессионного хранилища.
   */
  private referrerPathType?: string

  private user: User

  private urlReferrer: string

  private apiUrl?: string

  private commonPushData?: ProAnalyticsEventCustomProps

  constructor({
    statusCode,
    isBot,
    href,
    urlReferrer,
    user,
    apiUrl,
    commonPushData,
  }: {
    statusCode: number
    isBot: boolean
    href: string
    urlReferrer: string
    user: User
    apiUrl?: string
    commonPushData?: ProAnalyticsEventCustomProps
  }) {
    this.statusCode = statusCode || 200
    this.isBot = isBot
    this.href = href
    this.pathType = ProAnalytics.getPathType(this.href)
    this.user = user
    this.urlReferrer = urlReferrer
    this.apiUrl = apiUrl
    this.commonPushData = commonPushData

    this.savePageView()

    // Если известно, с какого адреса мы попали на текущую страницу,
    // есть смысл поискать в хранилище её pageViewId.
    if (urlReferrer) {
      const referrerPageView = this.restorePageView(urlReferrer)
      this.referrerPathType = referrerPageView?.[1]
      this.referrerPageViewId = referrerPageView?.[2]
    }
  }

  /**
   * Считывает лог сгенерированных pageViewId из хранилища.
   * Записи в логе имеют вид кортежей [href, pathType, pageViewId].
   */
  private readPageViewReflog(): [
    href: string,
    pathType: string,
    pageViewId: string
  ][] {
    const reflogJson = getLocalData(this.reflogStorageName, 'sessionStorage')
    let reflog
    if (reflogJson)
      try {
        reflog = JSON.parse(reflogJson)
        if (
          !(
            Array.isArray(reflog) &&
            reflog.every(
              (value) =>
                Array.isArray(value) &&
                typeof value[0] == 'string' &&
                typeof value[1] == 'string' &&
                typeof value[2] == 'string'
            )
          )
        ) {
          throw new TypeError('Invalid pro-analytics reflog format.')
        }
        return reflog
      } catch {
        deleteLocalData(this.reflogStorageName, 'sessionStorage')
      }
    return []
  }

  /**
   * Сохраняет сгенерированный pageViewId в лог,
   * для того, чтобы другая страница, ссылающаяся на данную страницу через referrer,
   * могла извлечь pageViewId данной страницы из хранилища
   * и отправить его как referrerPageViewId.
   */
  private savePageView() {
    const newRecord = [this.href, this.pathType, this.pageViewId]
    const reflog = this.readPageViewReflog()

    setLocalData(
      this.reflogStorageName,
      JSON.stringify([newRecord, ...reflog].slice(0, this.reflogSize)),
      'sessionStorage'
    )
  }

  /**
   * Ищет запись в логе pageViewId по соответствующему href.
   * Ищется только первая запись, и это конечно может приводить к ошибочным
   * подстановкам referrerPageViewId, если в логе есть несколько одинаковых href
   * и страницы открывались и загружались хаотично, но точного способа сопоставления
   * нет, будем считать это неизбежным злом, которое не должно часто проявляться.
   * По крайней мере, referrerPathType будет соответствовать.
   */
  private restorePageView(href: string) {
    const reflog = this.readPageViewReflog()
    return reflog.find(([referrerHref]) => referrerHref == href)
  }

  /**
   * Вспомогательный метод для вычисления даты окончания суток.
   */
  static getDayEnd(): Date {
    const d = new Date()
    d.setHours(23, 59, 59, 999)
    return d
  }

  /**
   * Генерирует уникальный идентификатор просмотра страницы.
   */
  static generatePageViewId(): string {
    return `${uuidv4()}|${
      Date.now() * (Math.floor(Math.random() * 100) + 1)
    }|${uuidv4()}`
  }

  /**
   * Возвращает название раздела.
   * Название определяется по метаданным страницы ("feed-context", "path-type"),
   * или просто берётся первый сегмент адреса страницы.
   */
  static getPathType(href: string) {
    const pathType = document
      .querySelector<HTMLMetaElement>('meta[name="path-type"]')
      ?.getAttribute('content')

    // Значение атрибута <meta name="path-type" content="?">
    // Это специальный атрибут для кастомизации pathType отдельных разделов.
    if (pathType) {
      return pathType
    }

    const { type: feedType, customPathType } =
      document.querySelector<HTMLMetaElement>('meta[name="feed-context"]')
        ?.dataset || {}

    // Значение атрибута <meta name="feed-context" data-custom-path-type="?">
    // Поддерживаем для совместимости, но лучше использовать мету "path-type".
    if (customPathType) {
      return customPathType
    }
    // Значение атрибута <meta name="feed-context" data-type="?">
    // Поддерживаем для совместимости, но лучше использовать мету "path-type".
    if (feedType) {
      return feedType
    }

    if (!href) {
      return null
    }
    try {
      return getFirstPartOfPath(new URL(href))
    } catch {
      return null
    }
  }

  /**
   * Отправляет событие в аналитику.
   * @param {Object} event
   * @returns {Promise}
   */
  private pushAnalyticsData(event: object): Promise<void> {
    return new Promise((resolve, reject) => {
      const isSuccess = navigator.sendBeacon(
        this.apiUrl || '/api/v1/analytics/push',
        JSON.stringify(event)
      )
      if (isSuccess) resolve()
      else reject()
    })
  }

  /**
   * Выполняет инициализацию аналитики: создаётся сессия
   * и отправляются события начала сессии и просмотра страницы.
   */
  public start(pushView: boolean = false) {
    if (!this.hasStarted && !this.isBot) {
      // Сессия стартует только в активном окне браузера
      // См. https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
      if (document.visibilityState == 'visible') {
        this.hasStarted = true
        if (pushView){
          this.initPageView()
        }
        this.setEvents()
        this.pushSession()
      }
      // Неактивное окно подписывается на изменение состояния,
      // чтобы начать сессию после первой активации окна
      else if (typeof document.visibilityState !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          this.start()
        })
      }
    }
    return this
  }

  /**
   * Отправляет автоматически отслеживаемое событие просмотра страницы.
   */
  private initPageView() {
    return this.pushEvent('page:view')
  }

  /**
   * Создаёт объект события, готовый к отправке на сервер.
   */
  private createEvent<T extends ProAnalyticsEventCustomProps>(
    name: string | string[],
    props?: T,
    extendedMeta: boolean = false
  ) {
    // Запрещаем свойство props.event, чтобы избежать неоднозначности
    // при одновременной передаче name и props.event, логически имеющих одинаковое назначение
    if (props && Object.prototype.hasOwnProperty.call(props, 'event')) {
      throw new Error(
        'Illegal invocation: property name "event" is reserved for event name.'
      )
    }
    // Итоговое название события
    // Представляет собой строку без пробелов, которая может быть разделена запятыми
    //
    // Приведение переданного имени к массиву необходимо для реализации единого сценария
    // как для одного имени, переданного строкой, так и для массива имен.
    const namesArray = [name].flat().filter((item) => item)

    // Дубликаты имён удаляются.
    const uniqueNames = [...new Set(namesArray).values()]

    const event = uniqueNames
      .toString()
      .split(',')
      .map((n) => n.replace(/\s+/g, ''))
      .filter((n) => n)
      .join(',')

    // Параметры рекламных кампаний (UTM-метки),
    // которые могут быть переданы через адресную строку.
    const {
      utm_campaign: utmCampaign,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_content: utmContent,
      utm_term: utmTerm
    } = query(decodeURI(window.location.search))

    return {
      event,
      pageViewId: this.pageViewId,
      userSession: this.getSession(),
      userAgent: window.navigator.userAgent,
      userId: this.user.id,
      userType: this.user.userType,
      isProSeller: this.user.isProfessional,
      path: this.href,
      pathType: this.pathType,
      referrer: this.urlReferrer === '' ? null : this.urlReferrer,
      referrerPageViewId: this.referrerPageViewId,
      referrerPathType: this.referrerPathType,
      cookieConsentGranted: CookieManager.getConsent() ?? 'undefined',
      utmCampaign,
      utmSource,
      utmMedium,
      utmContent,
      utmTerm,
      ...(extendedMeta ? { pageResponse: this.statusCode } : null),
      ...props
    }
  }

  /**
   * Возвращает идентификатор текущей сессии.
   *
   * @todo Этот метод должен быть приватным,
   * но вынужденно сделан публичным для костыля в Product.ts.
   */
  public getSession() {
    return getCookie(this.sessionCookieName)
  }

  /**
   * Устанавливает корневую сессионную куку.
   */
  private setSession(value: string) {
    const dayEnd = ProAnalytics.getDayEnd()
    // Время жизни сессии ограничено текущими сутками,
    // в начале суток всегда начинается новая сессия
    let sessionEnd = new Date(Date.now() + this.sessionDuration)
    if (sessionEnd > dayEnd) {
      sessionEnd = dayEnd
    }
    setCookie(
      this.sessionCookieName,
      value,
      {
        path: '/',
        expires: sessionEnd
      },
      true /* setRawValue */
    )
  }

  /**
   * Начинает, или продлевает сессию пользователя
   * Promise возвращает идентификатор созданной сессии,
   * идентификатор продлённой сессии не возвращается.
   */
  private startSession() {
    return new Promise<string | undefined>((resolve, reject) => {
      let sessionValue = this.getSession()
      if (!this.hasStarted) {
        reject(new Error('PRO-analytics has not been started yet.'))
      }
      if (sessionValue && sessionValue !== 'undefined') {
        // Текущая сессия продлевается
        this.setSession(sessionValue)
        resolve(undefined)
      } else {
        // При отсутствии сессии начинается новая
        sessionValue = `${Date.now()}|${uuidv4()}`
        this.setSession(sessionValue)
        resolve(sessionValue)
      }
    })
  }

  /**
   * Начинает, или продлевает сессию пользователя
   * и отправляет событие начала сессии на бекенд.
   *
   * @todo Этот метод должен быть приватным,
   * но вынужденно сделан публичным для костыля в Product.ts.
   */
  public pushSession() {
    return this.startSession().then((value) => {
      // Начало новой сессии отмечается специальным событием
      if (value) {
        return this.pushAnalyticsData(
          this.createEvent('page:userSessionStart', this.commonPushData, true)
        )
      }
      return undefined
    })
  }

  /**
   * Настраивает обработчики событий, приводящих к продлению сессии,
   * или пересозданию истёкшей сессии.
   */
  private setEvents() {
    // https://developer.mozilla.org/ru/docs/Web/Events
    const documentEvents = ['submit']

    // hashchange: The fragment identifier of the URL has changed (the part of the URL after the #).
    // popstate: A session history entry is being navigated to (in certain cases).
    const windowEvents = ['hashchange', 'popstate']

    documentEvents.forEach((event) =>
      document.addEventListener(event, () => {
        this.pushSession()
      })
    )

    windowEvents.forEach((event) =>
      window.addEventListener(event, () => {
        this.pushSession()
      })
    )

    setInterval(() => {
      if (window.location.href != this.href) {
        this.href = window.location.href
        this.pushSession()
      }
    }, 5000)
  }

  /**
   * Отправляет пользовательское событие.
   * @param name - название события (или массив названий)
   * @param props - дополнительные данные по событию
   * @param extendedMeta - передать полные метаданные по событию (page:userSessionStart, page:view)
   */
  public pushEvent<
    T extends ProAnalyticsEventCustomProps = ProAnalyticsEventCustomProps
  >(name: string | string[], props?: T, extendedMeta: boolean = false) {
    const commonPushData = this.commonPushData as T
    const eventProperties = { ...commonPushData, ...props }
    // Возникновение пользовательского события автоматически продлевает сессию
    return this.pushSession().then(() =>
      this.pushAnalyticsData(
        this.createEvent<T>(name, eventProperties, extendedMeta)
      )
    )
  }

  /**
   * Отправляет пользовательское событие,
   * полученное и виде простого объекта.
   * @param data - объект с данными о событии (наличие поля name обязательно)
   */
  public pushData(data: PushData) {
    if (data && data.name) {
      const { name, ...props } = data
      return this.pushEvent(name, props)
    }
    return Promise.reject()
  }
}

export default ProAnalytics
